// Chemin: C:\gestion-copro\src\pages\admin\VerificationDetailPage.tsx
// Session 67 : Phase 2 - Page détail vérification comptable
// Session 68 : Phase 3 - Ajout bouton analyse SENTINELLE + modification titre/notes
// Session 69 : Ajout dropzone upload direct (plus besoin de passer par la GED)
// Session 70 : Intégrations Courriers SCUD et Affaires depuis les contrôles
// Session 71 : Correction workflow - communication courante = pas de vote CS, envoi direct

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import {
  getVerificationById,
  getControlesByVerification,
  ajouterDocumentVerification,
  retirerDocumentVerification,
  traiterControle,
  cloturerVerification,
  lancerAnalyseComptable,
  updateVerification,
  ResultatAnalyseComptable,
  updateControle,
} from '../../services/verification.service';
import { getDocuments, uploadDocument } from '../../services/document.service';
import { createCourrier } from '../../services/courrier.service';
import { createAffaire, addEvenementToAffaire } from '../../services/affaire.service';
import { createMessageCS } from '../../services/messageCS.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
import {
  Verification,
  Controle,
  STATUT_VERIFICATION_LABELS,
  STATUT_VERIFICATION_COLORS,
  TYPE_CONTROLE_LABELS,
  STATUT_CONTROLE_LABELS,
  STATUT_CONTROLE_COLORS,
  getScoreColor,
  getScoreLabel,
} from '../../types/verification.types';
import { Document } from '../../types/document.types';
import { TYPES_DOCUMENTS_COMPTABLES } from '../../constants/documentTaxonomy';
import {
  genererObjetContestationComptable,
  genererContenuContestationComptable,
  genererTitreAffaireComptable,
  genererDescriptionAffaireComptable,
} from '../../utils/courrierComptableUtils';
import {
  ArrowLeft,
  Calculator,
  FileText,
  Plus,
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  FileCheck,
  Sparkles,
  RefreshCw,
  Pencil,
  Upload,
  File,
  Download,
  Mail,
  FolderOpen,
  ExternalLink,
  MessageSquare,
} from 'lucide-react';
import { telechargerPDFVerification } from '../../services/pdfVerification.service';

export default function VerificationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { coproprieteId, copropriete } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();

  const [verification, setVerification] = useState<Verification | null>(null);
  const [controles, setControles] = useState<Controle[]>([]);
  const [documentsLies, setDocumentsLies] = useState<Document[]>([]);
  const [documentsDisponibles, setDocumentsDisponibles] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [showTraiterModal, setShowTraiterModal] = useState<Controle | null>(null);
  const [traiterStatut, setTraiterStatut] = useState<'justifie' | 'conteste' | 'signale_ag'>('justifie');
  const [traiterNote, setTraiterNote] = useState('');
  const [traiterLoading, setTraiterLoading] = useState(false);

  // État analyse SENTINELLE
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [analyseResultat, setAnalyseResultat] = useState<ResultatAnalyseComptable | null>(null);
  const [analyseErreur, setAnalyseErreur] = useState<string | null>(null);

  // État modale modification
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitre, setEditTitre] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // SESSION 69 : DROPZONE UPLOAD DIRECT
  const [isDragging, setIsDragging] = useState(false);
  const [uploadEnCours, setUploadEnCours] = useState(false);
  const [uploadProgression, setUploadProgression] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // État génération PDF
  const [pdfEnCours, setPdfEnCours] = useState(false);

  // SESSION 70 : États pour intégrations Courriers/Affaires
  const [creationCourrierEnCours, setCreationCourrierEnCours] = useState(false);
  const [creationAffaireEnCours, setCreationAffaireEnCours] = useState(false);
  const [creationMessageEnCours, setCreationMessageEnCours] = useState(false);

  useEffect(() => {
    if (!coproprieteId || !id) return;
    loadData();
  }, [coproprieteId, id]);

  const loadData = async () => {
    if (!coproprieteId || !id) return;
    setLoading(true);
    try {
      const verif = await getVerificationById(coproprieteId, id);
      if (!verif) { setError('Vérification non trouvée'); return; }
      setVerification(verif);

      const ctrl = await getControlesByVerification(coproprieteId, id);
      setControles(ctrl);

      const allDocs = await getDocuments(coproprieteId);
      const docsComptables = allDocs.filter(
        (d) => d.categorie === 'comptabilite' || TYPES_DOCUMENTS_COMPTABLES.includes(d.type as any)
      );
      setDocumentsLies(docsComptables.filter((d) => verif.documentsIds.includes(d.id)));
      setDocumentsDisponibles(docsComptables.filter((d) => !verif.documentsIds.includes(d.id)));
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (documentId: string) => {
    if (!coproprieteId || !id) return;
    try {
      await ajouterDocumentVerification(coproprieteId, id, documentId);
      const doc = documentsDisponibles.find((d) => d.id === documentId);
      if (doc) {
        setDocumentsLies((prev) => [...prev, doc]);
        setDocumentsDisponibles((prev) => prev.filter((d) => d.id !== documentId));
        setVerification((prev) => prev ? { ...prev, documentsIds: [...prev.documentsIds, documentId] } : null);
      }
    } catch (err) { console.error(err); }
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!coproprieteId || !id) return;
    try {
      await retirerDocumentVerification(coproprieteId, id, documentId);
      const doc = documentsLies.find((d) => d.id === documentId);
      if (doc) {
        setDocumentsDisponibles((prev) => [...prev, doc]);
        setDocumentsLies((prev) => prev.filter((d) => d.id !== documentId));
        setVerification((prev) => prev ? { ...prev, documentsIds: prev.documentsIds.filter((did) => did !== documentId) } : null);
      }
    } catch (err) { console.error(err); }
  };

  const handleTraiterControle = async () => {
    if (!coproprieteId || !id || !showTraiterModal || !firebaseUser || !userProfile) return;
    setTraiterLoading(true);
    try {
      const userName = userProfile.prenom ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() : userProfile.email || '';
      await traiterControle(coproprieteId, id, showTraiterModal.id, { statut: traiterStatut, noteCS: traiterNote || undefined }, firebaseUser.uid, userName);
      setControles((prev) => prev.map((c) => c.id === showTraiterModal.id ? { ...c, statut: traiterStatut, noteCS: traiterNote } : c));
      setShowTraiterModal(null);
      setTraiterNote('');
    } catch (err) { console.error(err); }
    finally { setTraiterLoading(false); }
  };

  const handleCloturer = async () => {
    if (!coproprieteId || !id || !firebaseUser || !userProfile) return;
    if (!window.confirm('Clôturer cette vérification ?')) return;
    try {
      const userName = userProfile.prenom ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() : userProfile.email || '';
      await cloturerVerification(coproprieteId, id, firebaseUser.uid, userName);
      setVerification((prev) => prev ? { ...prev, statut: 'termine' } : null);
    } catch (err) { console.error(err); }
  };

  // ============================================
  // MODIFICATION VÉRIFICATION
  // ============================================
  const openEditModal = () => {
    if (!verification) return;
    setEditTitre(verification.titre || '');
    setEditNotes((verification as any).notes || '');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!coproprieteId || !id) return;
    setEditLoading(true);
    try {
      await updateVerification(coproprieteId, id, {
        titre: editTitre || undefined,
        notes: editNotes || undefined,
      } as any);
      setVerification((prev) => prev ? { ...prev, titre: editTitre, notes: editNotes } as any : null);
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setEditLoading(false);
    }
  };

  // ============================================
  // ANALYSE SENTINELLE
  // ============================================
  const handleLancerAnalyse = async () => {
    if (!coproprieteId || !id || !verification) return;
    
    setAnalyseEnCours(true);
    setAnalyseErreur(null);
    setAnalyseResultat(null);

    try {
      const resultat = await lancerAnalyseComptable(
        coproprieteId,
        id,
        verification.exercice
      );

      setAnalyseResultat(resultat);

      if (resultat.success) {
        // Recharger les contrôles après l'analyse
        const ctrl = await getControlesByVerification(coproprieteId, id);
        setControles(ctrl);

        // Recharger la vérification pour le score mis à jour
        const verif = await getVerificationById(coproprieteId, id);
        if (verif) setVerification(verif);
      }
    } catch (err: any) {
      console.error('Erreur analyse SENTINELLE:', err);
      setAnalyseErreur(err.message || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyseEnCours(false);
    }
  };

  // ============================================
  // SESSION 69 : UPLOAD DIRECT VIA DROPZONE
  // ============================================
  
  // Détection du type de document comptable à partir du nom de fichier
  const detectTypeDocument = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('appel') && lower.includes('fond')) return 'appel_fonds';
    if (lower.includes('budget')) return 'budget';
    if (lower.includes('annexe')) return 'annexe_comptable';
    if (lower.includes('relev')) return 'releve_charges';
    if (lower.includes('facture')) return 'facture';
    if (lower.includes('devis')) return 'devis';
    return 'autre'; // Par défaut
  };

  const handleFilesUpload = async (files: FileList | File[]) => {
    if (!coproprieteId || !id || !firebaseUser || !userProfile) return;
    
    const fileArray = Array.from(files);
    const pdfFiles = fileArray.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      alert('Veuillez sélectionner uniquement des fichiers PDF');
      return;
    }

    setUploadEnCours(true);
    const userName = userProfile.prenom ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() : userProfile.email || '';

    try {
      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i];
        setUploadProgression(`Upload ${i + 1}/${pdfFiles.length} : ${file.name}`);
        
        // Détecter le type de document
        const typeDoc = detectTypeDocument(file.name);
        
        // Titre basé sur le nom du fichier (sans extension)
        const titre = file.name.replace(/\.pdf$/i, '');

        // 1. Upload dans la GED avec catégorie comptabilite
        const documentId = await uploadDocument({
          coproprieteId,
          file,
          titre,
          type: typeDoc as any,
          categorie: 'comptabilite',
          description: `Document importe pour verification exercice ${verification?.exercice || ''}`,
          dateDocument: new Date(),
          visibilite: 'cs_only',
          userId: firebaseUser.uid,
          userNom: userName,
        });

        // 2. Lier automatiquement à la vérification en cours
        await ajouterDocumentVerification(coproprieteId, id, documentId);

        // 3. Mettre à jour l'état local
        const newDoc: Document = {
          id: documentId,
          coproprieteId,
          titre,
          type: typeDoc as any,
          categorie: 'comptabilite',
          fichierUrl: '', // Sera récupéré au reload
          fichierNom: file.name,
          fichierTaille: file.size,
          fichierType: file.type,
          dateDocument: { toDate: () => new Date() } as any,
          visibilite: 'cs_only',
          uploadePar: firebaseUser.uid,
          uploadeParNom: userName,
          createdAt: { toDate: () => new Date() } as any,
          updatedAt: { toDate: () => new Date() } as any,
        };

        setDocumentsLies(prev => [...prev, newDoc]);
        setVerification(prev => prev ? {
          ...prev,
          documentsIds: [...prev.documentsIds, documentId]
        } : null);
      }

      setUploadProgression(null);
      
    } catch (err: any) {
      console.error('Erreur upload:', err);
      alert(`Erreur lors de l'upload : ${err.message || 'Erreur inconnue'}`);
    } finally {
      setUploadEnCours(false);
      setUploadProgression(null);
    }
  };

  // Handlers Drag & Drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesUpload(e.dataTransfer.files);
    }
  }, [coproprieteId, id, firebaseUser, userProfile, verification]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesUpload(e.target.files);
    }
    // Reset input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  // ============================================
  // SESSION 69 : GÉNÉRATION RAPPORT PDF
  // ============================================
  const handleGeneratePDF = async () => {
    if (!coproprieteId || !verification) return;
    
    setPdfEnCours(true);
    try {
      // Nom de la copropriété depuis le contexte
      const nomCopro = copropriete?.nom || 'Copropriete';
      
      // Récupérer les noms des documents
      const documentsNoms = documentsLies.map(d => d.titre || d.fichierNom || 'Document');
      
      // Nom de l'utilisateur
      const userName = userProfile?.prenom 
        ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() 
        : userProfile?.email || 'Utilisateur';
      
      await telechargerPDFVerification({
        verification,
        controles,
        nomCopropriete: nomCopro,
        documentsNoms,
        generePar: userName,
      });
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      alert('Erreur lors de la génération du rapport PDF');
    } finally {
      setPdfEnCours(false);
    }
  };

  // ============================================
  // SESSION 70 : INTÉGRATION MESSAGERIE (demande simple)
  // ============================================
  const handleDemanderExplication = async (controle: Controle) => {
    if (!coproprieteId || !id || !verification || !firebaseUser || !userProfile) return;
    
    setCreationMessageEnCours(true);
    try {
      const userName = userProfile.prenom 
        ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() 
        : userProfile.email || '';
      const nomCopro = copropriete?.nom || 'Copropriete';

      // Générer l'objet du message
      const objet = `Demande d'explication - ${TYPE_CONTROLE_LABELS[controle.type] || controle.type} - Exercice ${verification.exercice}`;

      // Générer le contenu du message (moins formel qu'un courrier SCUD)
      let contenu = `Bonjour,\n\n`;
      contenu += `Dans le cadre de notre controle des comptes de l'exercice ${verification.exercice}, `;
      contenu += `nous avons releve un point necessitant des eclaircissements.\n\n`;
      contenu += `**Point concerne :** ${controle.description}\n`;
      if (controle.montant !== undefined) {
        contenu += `**Montant constate :** ${controle.montant.toLocaleString('fr-FR')} EUR\n`;
      }
      if (controle.montantAttendu !== undefined) {
        contenu += `**Montant attendu :** ${controle.montantAttendu.toLocaleString('fr-FR')} EUR\n`;
      }
      if (controle.ecart !== undefined) {
        contenu += `**Ecart :** ${controle.ecart.toLocaleString('fr-FR')} EUR\n`;
      }
      contenu += `\nPourriez-vous nous fournir des explications ou les justificatifs correspondants ?\n\n`;
      contenu += `Nous restons a votre disposition pour tout complement d'information.\n\n`;
      contenu += `Cordialement,\n`;
      contenu += `Le Conseil Syndical de ${nomCopro}`;

      // SESSION 71 : Créer le message en brouillon SANS soumission au vote
      // Une communication "courante" peut être envoyée directement par le secrétaire/admin
      await createMessageCS({
        coproprieteId,
        objet,
        contenu,
        destinataireEmail: '', // Sera renseigné lors de l'envoi
        destinataireNom: 'Syndic de copropriete',
        redacteurId: firebaseUser.uid,
        redacteurNom: userName,
        redacteurRole: 'membre_cs',
        statut: 'brouillon',
        analyseSentinelle: {
          risque: 'faible',
          nature: 'courante',
        },
      });

      // Fermer le modal et naviguer vers la messagerie (onglet En cours où sont les brouillons)
      setShowTraiterModal(null);
      navigate('/admin/messagerie');
      
    } catch (err: any) {
      console.error('Erreur création message:', err);
      alert(`Erreur lors de la création du message : ${err.message || 'Erreur inconnue'}`);
    } finally {
      setCreationMessageEnCours(false);
    }
  };

  // ============================================
  // SESSION 70 : INTÉGRATION COURRIERS SCUD
  // ============================================
  const handleContesterParCourrier = async (controle: Controle) => {
    if (!coproprieteId || !id || !verification || !firebaseUser || !userProfile) return;
    
    setCreationCourrierEnCours(true);
    try {
      const userName = userProfile.prenom 
        ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() 
        : userProfile.email || '';
      const nomCopro = copropriete?.nom || 'Copropriete';

      // Récupérer le nombre de membres CS pour votesRequis
      const membresCS = await getMembresCSByCopropriete(coproprieteId);
      const votesRequis = membresCS.length || 1;

      // Créer le courrier avec les données pré-remplies
      const courrierId = await createCourrier({
        coproprieteId,
        objet: genererObjetContestationComptable(controle, verification.exercice),
        contenu: genererContenuContestationComptable(controle, verification.exercice, nomCopro),
        type: 'demande_information',
        modeEnvoi: 'email',
        destinataire: {
          type: 'syndic',
          nom: 'Syndic de copropriete',
        },
        redacteurId: firebaseUser.uid,
        redacteurNom: userName,
      }, votesRequis);

      // Mettre à jour le contrôle avec l'ID du courrier
      await updateControle(coproprieteId, id, controle.id, {
        courrierId,
        statut: 'conteste',
      });

      // Mettre à jour l'état local
      setControles(prev => prev.map(c => 
        c.id === controle.id ? { ...c, courrierId, statut: 'conteste' } : c
      ));

      // Fermer le modal et naviguer vers le courrier
      setShowTraiterModal(null);
      navigate(`/admin/courriers/${courrierId}`);
      
    } catch (err: any) {
      console.error('Erreur création courrier:', err);
      alert(`Erreur lors de la création du courrier : ${err.message || 'Erreur inconnue'}`);
    } finally {
      setCreationCourrierEnCours(false);
    }
  };

  // ============================================
  // SESSION 70 : INTÉGRATION AFFAIRES
  // ============================================
  const handleCreerAffaire = async (controle: Controle) => {
    if (!coproprieteId || !id || !verification || !firebaseUser || !userProfile) return;
    
    setCreationAffaireEnCours(true);
    try {
      const userName = userProfile.prenom 
        ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() 
        : userProfile.email || '';

      // Créer l'affaire de type audit
      const affaireId = await createAffaire({
        coproprieteId,
        titre: genererTitreAffaireComptable(controle, verification.exercice),
        type: 'audit',
        priorite: controle.resultat === 'anomalie' ? 'haute' : 'normale',
        description: genererDescriptionAffaireComptable(controle, verification.exercice),
      }, firebaseUser.uid, userName);

      // Ajouter un événement à la timeline de l'affaire
      await addEvenementToAffaire(affaireId, {
        type: 'note',
        titre: 'Origine : Verification comptable',
        description: `Affaire creee depuis le module de verification comptable - Exercice ${verification.exercice}`,
        auteurId: firebaseUser.uid,
        auteurNom: userName,
      });

      // Mettre à jour le contrôle avec l'ID de l'affaire
      await updateControle(coproprieteId, id, controle.id, {
        affaireId,
      });

      // Mettre à jour l'état local
      setControles(prev => prev.map(c => 
        c.id === controle.id ? { ...c, affaireId } : c
      ));

      // Fermer le modal et naviguer vers l'affaire
      setShowTraiterModal(null);
      navigate(`/admin/affaires/${affaireId}`);
      
    } catch (err: any) {
      console.error('Erreur création affaire:', err);
      alert(`Erreur lors de la création de l'affaire : ${err.message || 'Erreur inconnue'}`);
    } finally {
      setCreationAffaireEnCours(false);
    }
  };

  const formatDate = (ts: any) => ts?.toDate ? ts.toDate().toLocaleDateString('fr-FR') : '-';
  const getResultatIcon = (r: string) => {
    if (r === 'ok') return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (r === 'attention') return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    if (r === 'anomalie') return <XCircle className="h-5 w-5 text-red-500" />;
    return null;
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 text-blue-600 animate-spin" /></div>;
  if (error || !verification) return <div className="p-6"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error || 'Non trouvée'}</div></div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/comptabilite')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft className="h-5 w-5 text-gray-500" /></button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3"><Calculator className="h-7 w-7 text-blue-600" />Exercice {verification.exercice}</h1>
            {verification.titre && <p className="text-gray-500">{verification.titre}</p>}
            {(verification as any).notes && <p className="text-sm text-gray-400 mt-1 italic">{(verification as any).notes}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUT_VERIFICATION_COLORS[verification.statut]}`}>{STATUT_VERIFICATION_LABELS[verification.statut]}</span>
          
          {/* Bouton Générer PDF - toujours visible si contrôles */}
          {controles.length > 0 && (
            <button
              onClick={handleGeneratePDF}
              disabled={pdfEnCours}
              className="flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
              title="Télécharger le rapport PDF"
            >
              {pdfEnCours ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Rapport PDF</span>
            </button>
          )}
          
          {verification.statut === 'en_cours' && (
            <>
              <button onClick={openEditModal} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Modifier">
                <Pencil className="h-5 w-5" />
              </button>
              <button onClick={handleCloturer} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Clôturer</button>
            </>
          )}
        </div>
      </div>

      {/* Score */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4 text-center">
          <div className={`text-3xl font-bold ${getScoreColor(verification.scoreConformite)}`}>{verification.scoreConformite}/100</div>
          <div className="text-sm text-gray-500">{getScoreLabel(verification.scoreConformite)}</div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3"><CheckCircle className="h-8 w-8 text-green-500" /><div><div className="text-2xl font-bold">{verification.controlesOK}</div><div className="text-sm text-gray-500">Conformes</div></div></div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3"><AlertTriangle className="h-8 w-8 text-amber-500" /><div><div className="text-2xl font-bold">{verification.controlesAttention}</div><div className="text-sm text-gray-500">A verifier</div></div></div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3"><XCircle className="h-8 w-8 text-red-500" /><div><div className="text-2xl font-bold">{verification.controlesAnomalie}</div><div className="text-sm text-gray-500">Anomalies</div></div></div>
      </div>

      {/* Documents + Dropzone */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-gray-500" />Documents comptables ({documentsLies.length})</h2>
          <button onClick={() => setShowAddDocModal(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Plus className="h-4 w-4" />Depuis GED</button>
        </div>

        {/* SESSION 69 : DROPZONE UPLOAD DIRECT */}
        {verification.statut === 'en_cours' && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              mb-4 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }
              ${uploadEnCours ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploadEnCours}
            />
            
            {uploadEnCours ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                <p className="text-sm text-blue-600 font-medium">{uploadProgression || 'Upload en cours...'}</p>
              </div>
            ) : (
              <>
                <Upload className={`h-10 w-10 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700">
                  Glissez vos documents PDF ici
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ou cliquez pour selectionner — Appels de fonds, annexes, budgets...
                </p>
              </>
            )}
          </div>
        )}

        {/* Liste des documents liés */}
        {documentsLies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>Aucun document lie</p>
            <p className="text-sm mt-1">Glissez des PDF ci-dessus ou ajoutez depuis la GED pour lancer l'analyse</p>
          </div>
        ) : (
          <div className="space-y-2">{documentsLies.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <File className="h-5 w-5 text-blue-500" />
                <div>
                  <div className="font-medium">{doc.titre}</div>
                  <div className="text-sm text-gray-500">{formatDate(doc.dateDocument)}</div>
                </div>
              </div>
              {verification.statut === 'en_cours' && (
                <button onClick={() => handleRemoveDocument(doc.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><X className="h-4 w-4" /></button>
              )}
            </div>
          ))}</div>
        )}

        {/* Bouton analyse SENTINELLE */}
        {documentsLies.length > 0 && verification.statut === 'en_cours' && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900">Analyse SENTINELLE</h3>
                <p className="text-sm text-purple-700 mt-1">
                  SENTINELLE va analyser vos {documentsLies.length} document{documentsLies.length > 1 ? 's' : ''} et detecter automatiquement les incoherences, ecarts et anomalies comptables.
                </p>
                
                {/* Message erreur */}
                {analyseErreur && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {analyseErreur}
                  </div>
                )}

                {/* Résultat analyse */}
                {analyseResultat && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${analyseResultat.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
                    {analyseResultat.success ? (
                      <>
                        <p className="font-medium">Analyse terminee</p>
                        <p>{analyseResultat.documentsAnalyses} document{analyseResultat.documentsAnalyses > 1 ? 's' : ''} analyse{analyseResultat.documentsAnalyses > 1 ? 's' : ''} — {analyseResultat.controlesAjoutes} controle{analyseResultat.controlesAjoutes > 1 ? 's' : ''} genere{analyseResultat.controlesAjoutes > 1 ? 's' : ''}</p>
                        {analyseResultat.erreurs.length > 0 && (
                          <p className="mt-1 text-amber-600">Avertissements : {analyseResultat.erreurs.join(', ')}</p>
                        )}
                      </>
                    ) : (
                      <p>{analyseResultat.erreurs.join(', ')}</p>
                    )}
                  </div>
                )}

                <button
                  onClick={handleLancerAnalyse}
                  disabled={analyseEnCours}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {analyseEnCours ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : controles.length > 0 ? (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Relancer l'analyse
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Lancer l'analyse
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contrôles */}
      {controles.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><FileCheck className="h-5 w-5 text-gray-500" />Resultats des controles ({controles.length})</h2>
          <div className="space-y-3">{controles.map((ctrl) => (
            <div key={ctrl.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="flex items-start gap-3">
                {getResultatIcon(ctrl.resultat)}
                <div>
                  <div className="font-medium">{TYPE_CONTROLE_LABELS[ctrl.type] || ctrl.type}</div>
                  <p className="text-sm text-gray-600 mt-1">{ctrl.description}</p>
                  {ctrl.regleAppliquee && <p className="text-xs text-gray-400 mt-1 italic">{ctrl.regleAppliquee}</p>}
                  {ctrl.montant !== undefined && (
                    <p className="text-sm text-gray-500 mt-1">
                      Montant : {ctrl.montant.toLocaleString('fr-FR')} EUR
                      {ctrl.montantAttendu !== undefined && <span className="text-gray-400"> (attendu : {ctrl.montantAttendu.toLocaleString('fr-FR')} EUR)</span>}
                      {ctrl.ecart !== undefined && <span className="text-red-600 ml-2">(ecart : {ctrl.ecart.toLocaleString('fr-FR')} EUR)</span>}
                    </p>
                  )}
                  {ctrl.noteCS && <p className="text-sm text-blue-600 mt-2 italic">Note CS : {ctrl.noteCS}</p>}
                  
                  {/* SESSION 70 : Liens vers courrier/affaire créés */}
                  {(ctrl.courrierId || ctrl.affaireId) && (
                    <div className="flex items-center gap-3 mt-2">
                      {ctrl.courrierId && (
                        <button
                          onClick={() => navigate(`/admin/courriers/${ctrl.courrierId}`)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <Mail className="h-3 w-3" />
                          Voir le courrier
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                      {ctrl.affaireId && (
                        <button
                          onClick={() => navigate(`/admin/affaires/${ctrl.affaireId}`)}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:underline"
                        >
                          <FolderOpen className="h-3 w-3" />
                          Voir l'affaire
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUT_CONTROLE_COLORS[ctrl.statut]}`}>{STATUT_CONTROLE_LABELS[ctrl.statut]}</span>
                {ctrl.statut === 'nouveau' && ctrl.resultat !== 'ok' && <button onClick={() => setShowTraiterModal(ctrl)} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Traiter</button>}
              </div>
            </div>
          ))}</div>
        </div>
      )}

      {/* Modal ajout doc depuis GED */}
      {showAddDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between"><h3 className="font-semibold">Ajouter depuis la GED</h3><button onClick={() => setShowAddDocModal(false)}><X className="h-5 w-5" /></button></div>
            <div className="p-4 overflow-y-auto max-h-96">
              {documentsDisponibles.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-500">Aucun document comptable disponible</p>
                  <p className="text-sm text-gray-400 mt-1">Utilisez la dropzone ci-dessus pour importer des PDF directement</p>
                </div>
              ) : (
                <div className="space-y-2">{documentsDisponibles.map((doc) => (
                  <button key={doc.id} onClick={() => { handleAddDocument(doc.id); setShowAddDocModal(false); }} className="w-full flex items-center gap-3 p-3 text-left hover:bg-blue-50 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-500" /><div className="flex-1"><div className="font-medium">{doc.titre}</div><div className="text-sm text-gray-500">{formatDate(doc.dateDocument)}</div></div><Plus className="h-5 w-5 text-gray-400" />
                  </button>
                ))}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal traiter - SESSION 70 : Ajout boutons Courrier et Affaire */}
      {showTraiterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="font-semibold mb-4">Traiter : {TYPE_CONTROLE_LABELS[showTraiterModal.type]}</h3>
            
            {/* Description du contrôle */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="text-gray-700">{showTraiterModal.description}</p>
              {showTraiterModal.montant !== undefined && (
                <p className="mt-2 text-gray-600">
                  Montant : <span className="font-medium">{showTraiterModal.montant.toLocaleString('fr-FR')} EUR</span>
                  {showTraiterModal.ecart !== undefined && (
                    <span className="text-red-600 ml-2">(ecart : {showTraiterModal.ecart.toLocaleString('fr-FR')} EUR)</span>
                  )}
                </p>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Actions rapides - SESSION 70 */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Actions rapides</label>
                <div className="grid grid-cols-1 gap-3">
                  {/* Demander explication - 80% des cas - EN PREMIER */}
                  <button
                    onClick={() => handleDemanderExplication(showTraiterModal)}
                    disabled={creationMessageEnCours || creationCourrierEnCours || creationAffaireEnCours}
                    className="flex items-center gap-3 p-4 border-2 border-blue-300 bg-blue-50 rounded-lg hover:bg-blue-100 hover:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creationMessageEnCours ? (
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                    ) : (
                      <MessageSquare className="h-6 w-6 text-blue-500" />
                    )}
                    <div className="text-left">
                      <span className="block text-sm font-medium text-blue-700">Demander une explication</span>
                      <span className="block text-xs text-gray-500">Email simple au syndic via Messagerie</span>
                    </div>
                  </button>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {/* Contester par courrier SCUD - 20% des cas */}
                    <button
                      onClick={() => handleContesterParCourrier(showTraiterModal)}
                      disabled={creationCourrierEnCours || creationAffaireEnCours || creationMessageEnCours}
                      className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-amber-300 rounded-lg hover:bg-amber-50 hover:border-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creationCourrierEnCours ? (
                        <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
                      ) : (
                        <Mail className="h-6 w-6 text-amber-500" />
                      )}
                      <span className="text-sm font-medium text-amber-700">Contestation formelle</span>
                      <span className="text-xs text-gray-500 text-center">Courrier SCUD tracable</span>
                    </button>
                    
                    {/* Créer une affaire */}
                    <button
                      onClick={() => handleCreerAffaire(showTraiterModal)}
                      disabled={creationCourrierEnCours || creationAffaireEnCours || creationMessageEnCours || !!showTraiterModal.affaireId}
                      className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-purple-300 rounded-lg hover:bg-purple-50 hover:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creationAffaireEnCours ? (
                        <Loader2 className="h-6 w-6 text-purple-500 animate-spin" />
                      ) : (
                        <FolderOpen className="h-6 w-6 text-purple-500" />
                      )}
                      <span className="text-sm font-medium text-purple-700">
                        {showTraiterModal.affaireId ? 'Affaire existante' : 'Ouvrir un dossier'}
                      </span>
                      <span className="text-xs text-gray-500 text-center">
                        {showTraiterModal.affaireId ? 'Deja liee' : 'Suivi long terme'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Séparateur */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="text-xs text-gray-400">ou marquer comme</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
              
              {/* Actions de marquage */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Marquer ce controle</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="radio" checked={traiterStatut === 'justifie'} onChange={() => setTraiterStatut('justifie')} className="text-green-600" />
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Justifie (le syndic a fourni une explication valable)</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="radio" checked={traiterStatut === 'signale_ag'} onChange={() => setTraiterStatut('signale_ag')} className="text-amber-600" />
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm">Signaler en AG (presenter aux coproprietaires)</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Note du conseil syndical</label>
                <textarea 
                  value={traiterNote} 
                  onChange={(e) => setTraiterNote(e.target.value)} 
                  rows={3} 
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Commentaire optionnel..." 
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowTraiterModal(null)} 
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button 
                onClick={handleTraiterControle} 
                disabled={traiterLoading || creationCourrierEnCours || creationAffaireEnCours || creationMessageEnCours} 
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {traiterLoading ? 'En cours...' : 'Valider le marquage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal modification */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Modifier la verification
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titre / Commentaire</label>
                <input
                  type="text"
                  value={editTitre}
                  onChange={(e) => setEditTitre(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ex: Controle pre-AG 2026"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes generales</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Notes internes sur cette verification..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={handleSaveEdit} disabled={editLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">{editLoading ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
